import { useEffect, useRef, useState } from 'react';
import { localStateStore } from '@/utils/localStateStore';

type TabLock = {
  tabId: string;
  updatedAt: number;
};

const LOCK_KEY = 'veeqWebPlayerActiveTabLock';
const LOCK_TTL_MS = 15000;
const HEARTBEAT_MS = 5000;

function createTabId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `tab_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function readLock(): TabLock | null {
  return localStateStore.getJson<TabLock>(LOCK_KEY, null);
}

function isStale(lock: TabLock | null): boolean {
  return !lock || Date.now() - lock.updatedAt > LOCK_TTL_MS;
}

export function useSingleTabLock() {
  const tabIdRef = useRef(createTabId());
  const [isActiveTab, setIsActiveTab] = useState(true);

  useEffect(() => {
    const tabId = tabIdRef.current;

    const claim = () => {
      const lock = readLock();
      const canClaim = !lock || lock.tabId === tabId || isStale(lock);

      if (canClaim) {
        localStateStore.setJson(LOCK_KEY, { tabId, updatedAt: Date.now() });
        setIsActiveTab(true);
        return;
      }

      setIsActiveTab(false);
    };

    const release = () => {
      const lock = readLock();
      if (lock?.tabId === tabId) {
        localStateStore.remove(LOCK_KEY);
      }
    };

    claim();
    const interval = window.setInterval(claim, HEARTBEAT_MS);
    window.addEventListener('storage', claim);
    window.addEventListener('beforeunload', release);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('storage', claim);
      window.removeEventListener('beforeunload', release);
      release();
    };
  }, []);

  return { isActiveTab };
}
